import { useEffect } from 'react';

/**
 * Blur any focused INPUT / TEXTAREA / contentEditable when the app returns
 * to the foreground, so the on-screen keyboard does not pop back up if the
 * user had a field focused before backgrounding. Touch-device only — desktop
 * focus is preserved across tab switches.
 *
 * Listens to three signals (each covers a different platform path):
 *   - visibilitychange (Android Chrome PWA, generic tab visibility)
 *   - pageshow + event.persisted (iOS Safari bfcache restore)
 *   - window focus (foreground transitions / Cmd-Tab back)
 *
 * Mount once at the app root.
 */
export function useDismissKeyboardOnReturn() {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!window.matchMedia?.('(pointer: coarse)').matches) return;

    function dismiss() {
      const el = document.activeElement;
      if (!(el instanceof HTMLElement)) return;
      if (el === document.body) return;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable) {
        el.blur();
      }
    }

    // Android Chrome opens the soft keyboard on every programmatic .focus()
    // call (iOS Safari doesn't). When the app returns and the destination
    // route mounts a component with autoFocus / inputRef.current?.focus(), the
    // keyboard pops up AFTER our initial dismiss runs. Cascade the blur
    // across the React commit + useEffect lifecycle to catch those late
    // focuses too. 300ms is a safe upper bound — users won't have started
    // typing that fast after foregrounding the app.
    function dismissCascaded() {
      dismiss();
      setTimeout(dismiss, 0);
      setTimeout(dismiss, 100);
      setTimeout(dismiss, 300);
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') dismissCascaded();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      // Skip the initial cold-load fire (no input is focused yet anyway).
      // bfcache restore on iOS sets persisted=true, which is what we want.
      if (e.persisted) dismissCascaded();
    };
    const onFocus = () => dismissCascaded();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);

    // Cold-load dismiss. iOS PWAs launched from the home screen sometimes
    // open with an input focused (Safari restores focus state across cold
    // launches, and a deep-linked route may mount an autoFocus'd field
    // before the user has tapped anything). Cascade the blur the same way
    // we do on foregrounding so any late mount-time focus is also caught.
    dismissCascaded();

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
    };
  }, []);
}
